import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getUniversityResourceRequest } from './MainScreenAPI';
import { RootState } from '../store';

// Define a type for the slice state
type UnivercityConfig = {
  id: number;
  analyticsKey: string;
  languages: string[];
  labelInfo: {
    countryLabel: string;
    programmeLabel: string;
    courseLabel: string;
  };
  hotjarConfig: {
    properties: {
      key: string;
      value: string;
    };
  };
  analyticsConfig: {
    properties: {
      key: string;
      value: string;
    };
  };
  prospectVerificationEnabled: boolean;
  applicationNumEnabled: boolean;
  courseEnabled: boolean;
  hotJarEnabled: boolean;
  paginationEnable: boolean;
  countrySearchEnabled: boolean;
  programmeEnabled: boolean;
  buddyTypes: [];
  activeMultiLocations: boolean;
  buddyVideoEnable: boolean;
  enableFeed?: boolean;
  noBuddyFormEnabled?: boolean;
  recaptureEnabled?: boolean;
  googleRecaptchaKey?: string;
  stateEnabled: boolean;
  states: StateProp[];
  customCityFilterEnabled: boolean;
  enableCourseToSayHelloForm: boolean;
};

export type StateProp = {
  id: number;
  name: string;
  programmes: Programmes[];
};

type Country = {
  id: string;
  name: string;
  code: string;
  flag: string;
};

export type Programmes = {
  id: number;
  name: string;
  displayName: string;
  courses: CoursesProp[];
};
type LanguageProp = {
  code: string;
  name: string;
  title: string;
  flag: string;
};

type CountryProp = {
  id: number;
  name: string;
  code: string;
  flag: string;
};

type LocationProp = {
  id: number;
  locationName: string;
};

export type CoursesProp = {
  id: number;
  name: string;
  displayName: string;
};

export interface UniversityResourceState {
  id: string | null;
  domain: string;
  name: string;
  chatSuggestions: {
    id: number;
    message: string;
  }[];
  theme: {
    id: string | null;
    shortName: string;
    themeColor: string;
    secondaryColor: string;
    logoUrl: string;
    faviconUrl: string;
    headerMessage: string;
    contentMessage: string;
  };
  receiveEmail: string;
  countries: CountryProp[];
  buddyCountries: CountryProp[];
  locations: LocationProp[];
  programmes: Programmes[];
  courses: CoursesProp[];
  univeristyConfig: UnivercityConfig;
  serverTimezone: string;
  minOffset: string;
  states: StateProp[];
}

interface UniversityResourceResponseProp {
  status: string;
  isFetching: boolean;
  payload: UniversityResourceState | null;
}

// Define the initial state using that type
const initialState: UniversityResourceResponseProp = {
  status: 'init',
  payload: null,

  isFetching: false,
};

export const fetchUniversityResourceById = createAsyncThunk('get/university/resource', async (id: string) => {
  return await getUniversityResourceRequest({ id });
});

// Then, handle actions in your reducers:
const MainScreenSlice = createSlice({
  name: 'getUniversityResource',
  initialState: initialState,
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(fetchUniversityResourceById.pending, (state) => {
        state.status = 'pending';
        state.isFetching = true;
      })
      .addCase(fetchUniversityResourceById.fulfilled, (state, action) => {
        // Add user to the state array
        state.status = 'success';
        state.isFetching = false;
        state.payload = action.payload;
      })
      .addCase(fetchUniversityResourceById.rejected, (state) => {
        state.status = 'failed';
        state.isFetching = false;
      });
  },
});

export const getUniversityResourceSelector = (state: RootState) => state.universityResource;
export default MainScreenSlice.reducer;
